import { ImageTagViewModel, ProductPriceViewModel } from '~/lib/data-contract';
import { ProductTagsLegacy, TextVariants, Typography } from '~/shared/components';
import { VisuallyHidden } from '../VisuallyHidden';
import { useFrame, useFranchise, useTranslation } from '~/shared/utils';
import styles from './price-group.module.css';
import clsx from 'clsx';
import { Spacer } from '~/theme';

export type PriceGroupProps = Pick<
    ProductPriceViewModel,
    'displayPrice' | 'displayUnitPrice' | 'displaySalePrice' | 'isOnSale' | 'currency'
> & {
    variant?: TextVariants;
    imageTags?: ImageTagViewModel[];
    isPDP?: boolean;
    orientation?: 'horizontal' | 'vertical-bottom' | 'vertical-top';
};

export const PriceGroup = ({
    displaySalePrice,
    displayPrice,
    displayUnitPrice,
    isOnSale,
    variant = 'body',
    orientation = 'horizontal',
    imageTags,
    isPDP,
}: PriceGroupProps) => {
    const { translate } = useTranslation();
    const { activeMarket } = useFranchise();
    const { data: frameData } = useFrame();

    const orientationStyles = clsx({
        [styles.horizontal]: orientation === 'horizontal',
        [styles.verticalTop]: orientation === 'vertical-top',
        [styles.verticalBottom]: orientation === 'vertical-bottom',
    });

    return (
        <>
            <div className={clsx(orientationStyles, styles.root)}>
                {isOnSale ? (
                    <Typography
                        className={clsx(styles.discounted, 'price-selector-on-sale')}
                        variant={variant}
                        as="span"
                    >
                        {displaySalePrice}
                    </Typography>
                ) : null}
                <div className={`${isOnSale ? styles.original : ''}`}>
                    <Typography variant={variant} as="span" className="price-selector">
                        <VisuallyHidden
                            children={translate(`price.${isOnSale ? 'previous' : 'current'}`)}
                        />
                        {displayPrice || displayUnitPrice}
                    </Typography>
                </div>
                {imageTags && imageTags.length > 0 && (
                    <ProductTagsLegacy productTags={imageTags} isCampaign />
                )}
            </div>
            <div className={clsx({ [styles.anydayWrapper]: isPDP })}>
                {frameData?.ecommerce?.anyDayWidget?.enable &&
                    frameData?.ecommerce?.anyDayWidget?.pricetagToken &&
                    isPDP &&
                    activeMarket?.currency === 'DKK' && (
                        <>
                            <Spacer xs="2" md="2" />
                            <anyday-price-widget
                                theme="outline"
                                currency={activeMarket.currency}
                                price-selector={
                                    !isOnSale ? '.price-selector' : '.price-selector-on-sale'
                                }
                                price-format-locale={`${activeMarket?.language}-${activeMarket?.countryKey}`}
                                locale={`${activeMarket?.language}-${activeMarket?.countryKey}`}
                                token={frameData.ecommerce.anyDayWidget.pricetagToken}
                                fully-clickable="true"
                                container-breakpoint="350"
                                debug="info"
                            ></anyday-price-widget>
                        </>
                    )}
            </div>
        </>
    );
};
